import React, { FunctionComponent } from "react";
import { connect } from "react-redux";
import { Paper } from "@mui/material";
import { Icon } from "@hxnova/icons";

import "../../../../css/nav_bar/nav_bar_secondary.css";
import getPageName from "../../../services/getters/location_metadata/get_page_name";
import NavBarExportDropdownMenu from "../nav_bar_export_dropdown_menu";
import NavBarFloorDropdown from "../shared/nav_bar_floor_dropdown";
import NavBarPanelsMenu from "../nav_bar_panels_menu";
import NavBarScanDropdown from "../shared/nav_bar_scan_dropdown";
import NavBarSecondaryModes from "./nav_bar_secondary_modes";
import NavBarSecondaryTrades from "./nav_bar_secondary_trades";
import PageNames from "../../../models/domain/enums/page_names";
import { useFlags } from "../../../services/utilities/launch_darkly_helper";

export type Props = ReturnType<typeof mapStateToProps>;

// Nova's own glyphs, so this is the same artwork the design was drawn with rather than a MUI
// stand-in. The icon font is loaded globally in src/index.tsx.
const BimIcon = <Icon family="nova" name="BIM" size={24}/>;
const RealityCaptureIcon = <Icon family="material" name="photo_camera" size={24}/>;
// The design draws every dropdown with keyboard_arrow_down, not MUI's filled arrow_drop_down.
// Passed in rather than changed in the shared dropdowns, so the legacy nav keeps its own.
// aria-hidden because this is a ligature glyph, not an SVG: without it the icon's own text
// lands in each button's accessible name ("Export keyboard_arrow_down").
const DropdownChevron = <Icon family="material" name="keyboard_arrow_down" size={24} aria-hidden="true"/>;

// The reskinned top nav renders page destinations only, so the Explorer's canvas-level controls
// live here instead. Shares the top nav's flag, so the legacy nav keeps them when it is off.
export const NavBarSecondary: FunctionComponent<Props> = ({ pageName }) => {
  const { newDesignExplorerView } = useFlags();

  if (!newDesignExplorerView || pageName !== PageNames.PROJECT_VIEWER) {
    return null;
  }

  // Not a <nav> landmark: these act on the canvas rather than navigating. "toolbar" would promise
  // arrow-key traversal we don't implement.
  return <Paper className="NavBarSecondary" square elevation={0} role="group" aria-label="Viewer controls">
    <div className="NavBarSecondary-left">
      <NavBarSecondaryModes/>
      <span className="NavBarSecondary-divider" aria-hidden="true"/>
      <div className="NavBarSecondary-scanAndBim">
        <NavBarFloorDropdown startIcon={BimIcon} endIcon={DropdownChevron}/>
        <NavBarScanDropdown startIcon={RealityCaptureIcon} endIcon={DropdownChevron}/>
      </div>
    </div>
    <div className="NavBarSecondary-right">
      <span className="NavBarSecondary-divider" aria-hidden="true"/>
      <NavBarExportDropdownMenu label="Export" endIcon={DropdownChevron}/>
      <NavBarSecondaryTrades icon={DropdownChevron}/>
      <NavBarPanelsMenu endIcon={DropdownChevron}/>
    </div>
  </Paper>;
};

const mapStateToProps = (state, props) => ({
  pageName: getPageName(state, props),
});

export default connect(mapStateToProps)(NavBarSecondary);
